import { getFavoritesServiceUrl, getFavoritesLabelsServiceUrl } from './urls';

export interface Favorite {
  Id: string;
  Content: {
    Id: string;
    Title: string;
    ContentType: string;
  };
  SiteId: string;
  [key: string]: unknown;
}

export interface FavoritesResponse {
  Favorites?: Favorite[];
  error?: string;
  error_description?: string;
}

export interface AddFavoriteRequest {
  Content: {
    Id: string;
    Title?: string;
    ContentType?: string;
  };
}

export interface CreateLabelRequest {
  Name: string;
}

export interface UpdateLabelRequest {
  Id: string;
  Name: string;
}

export interface DeleteLabelRequest {
  Id: string;
}

export interface DeleteLabelResponse {
  Success: boolean;
  error?: string;
  error_description?: string;
}

export interface LabelResponse {
  Success: boolean;
  Messages: Array<{
    Id: string;
    Text: string;
  }>;
  Label?: {
    Id: string;
    Name: string;
  };
  FavoriteCount: number;
  error?: string;
  error_description?: string;
}

/**
 * Get user favorites using bearer token (from user credentials)
 * @param accessToken - Bearer token obtained from login (user's access token)
 * @param options - Optional filters: labelIds (array), pageSize, page
 * @returns FavoritesResponse with favorites data or error
 */
export async function getFavorites(
  accessToken: string,
  options?: {
    labelIds?: string[];
    pageSize?: number;
    page?: number;
  }
): Promise<FavoritesResponse> {
  try {
    if (!accessToken) {
      return {
        error: 'MISSING_TOKEN',
        error_description: 'Access token is required',
      };
    }

    let favoritesUrl = getFavoritesServiceUrl();

    // Build query parameters
    const params = new URLSearchParams();
    if (options?.labelIds && options.labelIds.length > 0) {
      params.append('labelIds', options.labelIds.join(' '));
    }
    if (options?.pageSize) {
      params.append('pageSize', options.pageSize.toString());
    }
    if (options?.page) {
      params.append('page', options.page.toString());
    }

    if (params.toString()) {
      favoritesUrl = `${favoritesUrl}?${params.toString()}`;
    }

    const response = await fetch(favoritesUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error(`Error fetching favorites: ${response.statusText}`, errorData);

      return {
        error: 'FAVORITES_FETCH_FAILED',
        error_description: errorData.error_description || response.statusText,
      };
    }

    const responseData = await response.json();
    return responseData;
  } catch (error) {
    console.error(`Error fetching favorites: ${error}`);
    return {
      error: 'GENERIC_ERROR',
      error_description: error instanceof Error ? error.message : 'Unknown error occurred',
    };
  }
}

/**
 * Add a favorite using bearer token
 * @param accessToken - Bearer token obtained from login
 * @param favoriteData - Data for the favorite to add
 * @param labelIds - Optional array of label IDs to associate with the favorite
 * @returns FavoritesResponse with result or error
 */
export async function addFavorite(
  accessToken: string,
  favoriteData: AddFavoriteRequest,
  labelIds?: string[]
): Promise<FavoritesResponse> {
  try {
    if (!accessToken) {
      return {
        error: 'MISSING_TOKEN',
        error_description: 'Access token is required',
      };
    }

    if (!favoriteData?.Content?.Id) {
      return {
        error: 'INVALID_REQUEST',
        error_description: 'Content.Id is required',
      };
    }

    let favoritesUrl = getFavoritesServiceUrl();

    // Add labelIds as query parameter if provided
    if (labelIds && labelIds.length > 0) {
      // Strip hyphens from GUIDs to get N format, then join with space (becomes + in URL)
      const labelIdsParam = labelIds.map((id) => id.replace(/-/g, '')).join(' ');
      favoritesUrl = `${favoritesUrl}?labelIds=${encodeURIComponent(labelIdsParam)}`;
    }

    const response = await fetch(favoritesUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify(favoriteData),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error(`Error adding favorite: ${response.statusText}`, errorData);

      return {
        error: 'FAVORITE_ADD_FAILED',
        error_description: errorData.error_description || response.statusText,
      };
    }

    const responseData = await response.json();
    return responseData;
  } catch (error) {
    console.error(`Error adding favorite: ${error}`);
    return {
      error: 'GENERIC_ERROR',
      error_description: error instanceof Error ? error.message : 'Unknown error occurred',
    };
  }
}

/**
 * Delete a favorite using bearer token and contentId
 * @param accessToken - Bearer token obtained from login
 * @param contentId - Content ID of the favorite to delete
 * @returns FavoritesResponse with result or error
 */
export async function deleteFavorite(
  accessToken: string,
  contentId: string
): Promise<FavoritesResponse> {
  try {
    if (!accessToken) {
      return {
        error: 'MISSING_TOKEN',
        error_description: 'Access token is required',
      };
    }

    if (!contentId) {
      return {
        error: 'INVALID_REQUEST',
        error_description: 'ContentId is required',
      };
    }

    const favoritesUrl = getFavoritesServiceUrl();
    const url = `${favoritesUrl}?contentId=${encodeURIComponent(contentId)}`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-HTTP-Method-Override': 'DELETE',
        Authorization: `BEARER ${accessToken}`,
      },
      body: JSON.stringify({}),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error(`Error deleting favorite: ${response.statusText}`, errorData);

      return {
        error: 'FAVORITE_DELETE_FAILED',
        error_description: errorData.error_description || response.statusText,
      };
    }

    const responseData = await response.json().catch(() => ({}));
    return responseData;
  } catch (error) {
    console.error(`Error deleting favorite: ${error}`);
    return {
      error: 'GENERIC_ERROR',
      error_description:
        error instanceof Error ? error.message : 'Unknown error occurred while deleting favorite',
    };
  }
}

/**
 * Delete a favorite from specific labels using bearer token
 * @param accessToken - Bearer token obtained from login
 * @param contentId - Content ID of the favorite to delete
 * @param labelIds - Array of label IDs to remove the favorite from
 * @returns FavoritesResponse with result or error
 */
export async function deleteFavoriteFromLabels(
  accessToken: string,
  contentId: string,
  labelIds: string[]
): Promise<FavoritesResponse> {
  try {
    if (!accessToken) {
      return {
        error: 'MISSING_TOKEN',
        error_description: 'Access token is required',
      };
    }

    if (!contentId) {
      return {
        error: 'INVALID_REQUEST',
        error_description: 'ContentId is required',
      };
    }

    if (!labelIds || labelIds.length === 0) {
      return {
        error: 'INVALID_REQUEST',
        error_description: 'LabelIds are required',
      };
    }

    const favoritesUrl = getFavoritesServiceUrl();

    const response = await fetch(favoritesUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-HTTP-Method-Override': 'DELETE',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        ContentId: contentId,
        LabelIds: labelIds,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error(`Error deleting favorite from labels: ${response.statusText}`, errorData);

      return {
        error: 'FAVORITE_DELETE_FAILED',
        error_description: errorData.error_description || response.statusText,
      };
    }

    const responseData = await response.json().catch(() => ({}));
    return responseData;
  } catch (error) {
    console.error(`Error deleting favorite from labels: ${error}`);
    return {
      error: 'GENERIC_ERROR',
      error_description:
        error instanceof Error
          ? error.message
          : 'Unknown error occurred while deleting favorite from labels',
    };
  }
}

/**
 * Create a new label using bearer token
 * @param accessToken - Bearer token obtained from login
 * @param labelData - Data for the label to create
 * @returns LabelResponse with result or error
 */
export async function createLabel(
  accessToken: string,
  labelData: CreateLabelRequest
): Promise<LabelResponse> {
  try {
    if (!accessToken) {
      return {
        Success: false,
        Messages: [],
        FavoriteCount: 0,
        error: 'MISSING_TOKEN',
        error_description: 'Access token is required',
      };
    }

    if (!labelData?.Name) {
      return {
        Success: false,
        Messages: [],
        FavoriteCount: 0,
        error: 'INVALID_REQUEST',
        error_description: 'Name is required',
      };
    }

    const labelsUrl = getFavoritesLabelsServiceUrl();

    const response = await fetch(labelsUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify(labelData),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error(`Error creating label: ${response.statusText}`, errorData);

      return {
        Success: false,
        Messages: [],
        FavoriteCount: 0,
        error: 'LABEL_CREATE_FAILED',
        error_description: errorData.error_description || errorData.message || response.statusText,
        ...errorData,
      };
    }

    const responseData: LabelResponse = await response.json();

    // Check if label creation failed (duplicate name, etc.)
    if (!responseData.Success && responseData.Messages.length > 0) {
      console.warn(`Label creation failed: ${responseData.Messages[0].Text}`);
      return {
        ...responseData,
        error: 'LABEL_CREATION_FAILED',
        error_description: responseData.Messages[0].Text,
      };
    }

    return responseData;
  } catch (error) {
    console.error(`Error creating label: ${error}`);
    return {
      Success: false,
      Messages: [],
      FavoriteCount: 0,
      error: 'GENERIC_ERROR',
      error_description:
        error instanceof Error ? error.message : 'Unknown error occurred while creating label',
    };
  }
}

/**
 * Update a label using bearer token
 * @param accessToken - Bearer token obtained from login
 * @param labelData - Data for the label to update (Id and Name)
 * @returns LabelResponse with result or error
 */
export async function updateLabel(
  accessToken: string,
  labelData: UpdateLabelRequest
): Promise<LabelResponse> {
  try {
    if (!accessToken) {
      return {
        Success: false,
        Messages: [],
        FavoriteCount: 0,
        error: 'MISSING_TOKEN',
        error_description: 'Access token is required',
      };
    }

    if (!labelData?.Id || !labelData?.Name) {
      return {
        Success: false,
        Messages: [],
        FavoriteCount: 0,
        error: 'INVALID_REQUEST',
        error_description: 'Id and Name are required',
      };
    }

    const labelsUrl = getFavoritesLabelsServiceUrl();

    const response = await fetch(labelsUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-HTTP-Method-Override': 'PUT',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify(labelData),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error(`Error updating label: ${response.statusText}`, errorData);

      return {
        Success: false,
        Messages: [],
        FavoriteCount: 0,
        error: 'LABEL_UPDATE_FAILED',
        error_description: errorData.error_description || errorData.message || response.statusText,
        ...errorData,
      };
    }

    const responseData: LabelResponse = await response.json();

    // Check if label update failed
    if (!responseData.Success && responseData.Messages.length > 0) {
      console.warn(`Label update failed: ${responseData.Messages[0].Text}`);
      return {
        ...responseData,
        error: 'LABEL_UPDATE_FAILED',
        error_description: responseData.Messages[0].Text,
      };
    }

    return responseData;
  } catch (error) {
    console.error(`Error updating label: ${error}`);
    return {
      Success: false,
      Messages: [],
      FavoriteCount: 0,
      error: 'GENERIC_ERROR',
      error_description: error instanceof Error ? error.message : 'Unknown error occurred',
    };
  }
}

/**
 * Delete a label using bearer token
 * @param accessToken - Bearer token obtained from login
 * @param labelData - Data for the label to delete (Id)
 * @returns DeleteLabelResponse with result or error
 */
export async function deleteLabel(
  accessToken: string,
  labelData: DeleteLabelRequest
): Promise<DeleteLabelResponse> {
  try {
    if (!accessToken) {
      return {
        Success: false,
        error: 'MISSING_TOKEN',
        error_description: 'Access token is required',
      };
    }

    if (!labelData?.Id) {
      return {
        Success: false,
        error: 'INVALID_REQUEST',
        error_description: 'Id is required',
      };
    }

    const labelsUrl = `${getFavoritesLabelsServiceUrl()}?labelId=${labelData.Id}`;

    const response = await fetch(labelsUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-HTTP-Method-Override': 'DELETE',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify(labelData),
    });

    // 204 No Content is success for DELETE
    if (response.status === 204) {
      return {
        Success: true,
      };
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error(`Error deleting label: ${response.statusText}`, errorData);

      return {
        Success: false,
        error: 'LABEL_DELETE_FAILED',
        error_description: errorData.error_description || errorData.message || response.statusText,
        ...errorData,
      };
    }

    return {
      Success: true,
    };
  } catch (error) {
    console.error(`Error deleting label: ${error}`);
    return {
      Success: false,
      error: 'GENERIC_ERROR',
      error_description: error instanceof Error ? error.message : 'Unknown error occurred',
    };
  }
}
